import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import Icon from 'components/AppIcon';
import { Link } from 'react-router-dom';

const HumanResourcesManagementServicesPage = () => {
  const hrModules = [
    {
      icon: 'Laptop',
      title: 'Digitalisation et optimisation des processus RH (HR Tech)',
      description: 'Transformez votre fonction RH avec les technologies modernes pour une efficacité maximale.',
      features: [
        'Audit et cartographie des processus RH',
        'Mise en place et adoption d\'un SIRH',
        'Recrutement 4.0 et outils IA',
        'Dématérialisation de la gestion administrative RH'
      ]
    },
    {
      icon: 'Users',
      title: 'Acquisition, développement et fidélisation des talents',
      description: 'Attirez, développez et retenez les meilleurs talents pour propulser votre organisation.',
      features: [
        'Expérience collaborateur (Employee Experience)',
        'Marque employeur et réseaux sociaux professionnels',
        'GPEC et gestion des parcours professionnels',
        'Évaluation de la performance 360° et feedback continu'
      ]
    },
    {
      icon: 'Scale',
      title: 'Management social et droit du travail',
      description: 'Maîtrisez les aspects juridiques et sociaux pour une gestion RH conforme et harmonieuse.',
      features: [
        'Droit du travail pour managers',
        'Négociation sociale et relations professionnelles',
        'Diversité, équité et inclusion (DEI)',
        'Gestion des conflits et médiation'
      ]
    }
  ];

  const whyChooseUs = [
    {
      icon: 'Target',
      title: 'Approche Stratégique',
      description: 'Transformez la fonction RH en véritable partenaire stratégique de la croissance.'
    },
    {
      icon: 'TrendingUp',
      title: 'ROI Mesurable',
      description: 'Amélioration de 45% de la satisfaction employés et réduction de 60% du turnover.'
    },
    {
      icon: 'Award',
      title: 'Expertise Reconnue',
      description: 'Consultants RH certifiés avec expérience terrain dans divers secteurs d\'activité.'
    },
    {
      icon: 'Zap',
      title: 'Innovation RH',
      description: 'Intégration des dernières technologies et meilleures pratiques internationales.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Section 1: Hero avec Introduction */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(155, 142, 87, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(155, 142, 87, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
                <Icon name="Users" size={20} style={{ color: '#9B8E57' }} />
                <span className="text-sm font-semibold" style={{ color: '#9B8E57' }}>
                  Domaine d'Expertise
                </span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                Management des Ressources Humaines
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                Transformer la fonction RH en un véritable partenaire stratégique de la croissance. Placez l'humain au cœur de votre succès durable.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/contact" 
                  className="inline-flex items-center px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
                  style={{ background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)', color: 'white' }}
                >
                  <Icon name="MessageCircle" size={20} className="mr-2" />
                  Consultation Gratuite
                </Link>
                <Link 
                  to="/service-request-form" 
                  className="inline-flex items-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 border border-white/20"
                >
                  <Icon name="FileText" size={20} className="mr-2" />
                  Audit RH Gratuit
                </Link>
              </div>
            </div>

            {/* Right Visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[#96121D]/20 via-[#9B8E57]/20 to-[#96121D]/10 rounded-3xl p-12 backdrop-blur-sm border border-white/10">
                <Icon name="Users" size={200} style={{ color: '#9B8E57' }} className="mx-auto" />
              </div>
              <div 
                className="absolute -bottom-6 -right-6 px-6 py-4 rounded-2xl shadow-2xl"
                style={{ background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)' }}
              >
                <div className="flex items-center space-x-2">
                  <Icon name="Heart" size={24} className="text-white" />
                  <span className="text-sm font-semibold text-white">Capital Humain Excellence</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Modules RH */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Nos Modules de Formation RH</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Solutions complètes pour optimiser votre gestion des ressources humaines et développer votre capital humain
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {hrModules?.map((module, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-8 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 group"
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                  style={{ background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)' }}
                >
                  <Icon name={module?.icon} size={32} className="text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  {module?.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {module?.description}
                </p>
                
                <ul className="space-y-3">
                  {module?.features?.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <Icon name="CheckCircle" size={20} style={{ color: '#9B8E57' }} className="mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Pourquoi Nous Choisir */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Pourquoi Choisir CRF Perfection ?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une expertise RH reconnue pour transformer votre organisation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseUs?.map((benefit, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 text-center group"
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300"
                  style={{ background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)' }}
                >
                  <Icon name={benefit?.icon} size={28} className="text-white" />
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit?.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed">
                  {benefit?.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: CTA */}
      <section 
        className="py-20 text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #96121D 0%, #9B8E57 100%)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `
              linear-gradient(rgba(255, 255, 255, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255, 255, 255, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Prêt à Transformer Votre Fonction RH ?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Contactez-nous pour un audit RH gratuit et découvrez comment optimiser votre gestion des ressources humaines
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center px-8 py-4 bg-white rounded-xl font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
              style={{ color: '#96121D' }}
            >
              <Icon name="Phone" size={20} className="mr-2" />
              Contactez-nous
            </Link>
            <Link 
              to="/service-request-form" 
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-white/20 transition-all duration-300 border-2 border-white"
            >
              <Icon name="FileText" size={20} className="mr-2" />
              Demander un Devis
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HumanResourcesManagementServicesPage;